// Ops → Prompt Improvement (admin only). Downloads the prompt-review export:
// one PII-redacted JSONL row per decided AI suggestion, holding the inbound
// message, the drafter's reply, the grader's scores and notes, and the text
// the human actually committed. The file is what a prompt author (or an LLM
// asked to review it) reads to find where the drafter and the reviewer
// disagree. Nothing on this page changes data.
//
// Server: routes/admin-prompt-review.js (filters, role gate, streaming) and
// services/prompt-review-export.js (row shape, fail-closed redaction).

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup,
  InputLabel, ListSubheader, MenuItem, Paper, Select, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material'
import { downloadPromptReviewExport, fetchCompanySettings, fetchPromptReviewCounts } from '../../api.js'

const STATUSES = [
  { value: 'edited', label: 'Edited', hint: 'the reviewer changed the draft before sending — the strongest signal' },
  { value: 'approved', label: 'Approved', hint: 'sent as drafted' },
  { value: 'escalated', label: 'Escalated', hint: 'the reviewer would not send any AI reply' },
  { value: 'transferred', label: 'Transferred to Support', hint: 'moved to the Support team, no reply sent' },
  { value: 'dismissed', label: 'Dismissed', hint: 'no reply needed, or spam' },
]

// conversation_channel values → what a person calls them. The prompts differ
// per channel far more than per brand, so the channel is the first cut.
const CHANNELS = [
  { value: 'ebay', label: 'eBay' },
  { value: 'm365_email', label: 'Email' },
]
const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map((c) => [c.value, c.label]))

const LIMITS = [100, 200, 500, 1000]

// datetime-local wants "YYYY-MM-DDTHH:MM" in local time.
function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function toIso(local) {
  if (!local) return undefined
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

// One line that tells the accounts apart: brand, channel, and for a mailbox
// its address — the registry's display name alone is the brand, so two eBay
// and three email accounts all read "1A Auto" / "AM Autoparts" without it.
function accountLabel(a) {
  const brand = a.displayName ?? a.companyCode ?? a.company
  const parts = [brand, CHANNEL_LABEL[a.channel] ?? a.channel]
  if (a.outboundFrom) parts.push(a.outboundFrom)
  return parts.filter(Boolean).join(' · ')
}

const ERR_TEXT = {
  invalid_date: 'One of the dates is not valid.',
  empty_window: 'The "From" time must be before the "To" time.',
  unknown_account: 'That account is not registered.',
  invalid_status: 'Pick at least one status.',
}

export default function PromptReviewPage() {
  const [accounts, setAccounts] = useState([])
  const [channel, setChannel] = useState('')
  const [account, setAccount] = useState('')
  const [from, setFrom] = useState(() => toLocalInput(new Date(Date.now() - 30 * 86_400_000)))
  const [to, setTo] = useState(() => toLocalInput(new Date()))
  const [statuses, setStatuses] = useState(() => new Set(STATUSES.map((s) => s.value)))
  const [limit, setLimit] = useState(200)
  const [counts, setCounts] = useState(null)
  const [countsError, setCountsError] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [downloaded, setDownloaded] = useState(null)
  const [downloadError, setDownloadError] = useState(null)

  useEffect(() => {
    fetchCompanySettings().then(setAccounts).catch(() => setAccounts([]))
  }, [])

  // Accounts on the chosen channel, grouped by channel when none is chosen.
  const groupedAccounts = useMemo(() => {
    const visible = accounts.filter((a) => !channel || a.channel === channel)
    const groups = new Map()
    for (const a of visible) {
      const key = a.channel ?? 'other'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(a)
    }
    for (const list of groups.values()) list.sort((x, y) => accountLabel(x).localeCompare(accountLabel(y)))
    // eBay first, then email, then anything else the registry grows.
    const order = CHANNELS.map((c) => c.value)
    const rank = (ch) => { const i = order.indexOf(ch); return i === -1 ? 99 : i }
    return [...groups.entries()].sort(([a], [b]) => rank(a) - rank(b))
  }, [accounts, channel])

  const onChannel = (_e, next) => {
    // ToggleButtonGroup hands back null when the active button is clicked
    // again; treat that as "All channels".
    const value = next ?? ''
    setChannel(value)
    // An account from another channel cannot stay selected.
    if (value && account && accounts.find((a) => a.company === account)?.channel !== value) setAccount('')
  }

  const params = {
    from: toIso(from),
    to: toIso(to),
    channel: channel || undefined,
    account: account || undefined,
    statuses: [...statuses].join(','),
  }

  // Re-count whenever a filter changes so the button always states what it
  // will download. Debounced a little: date pickers fire per keystroke.
  const paramsKey = JSON.stringify(params)
  useEffect(() => {
    let cancelled = false
    const t = setTimeout(() => {
      setCountsError(null)
      fetchPromptReviewCounts(JSON.parse(paramsKey))
        .then((d) => { if (!cancelled) setCounts(d.counts) })
        .catch((e) => { if (!cancelled) { setCounts(null); setCountsError(String(e.message ?? e)) } })
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [paramsKey])

  const toggleStatus = (value) => {
    setStatuses((prev) => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value); else next.add(value)
      return next
    })
  }

  const onDownload = useCallback(async () => {
    setDownloading(true)
    setDownloadError(null)
    setDownloaded(null)
    try {
      const name = await downloadPromptReviewExport({ ...params, limit })
      setDownloaded(name)
    } catch (e) {
      const code = /-> 400/.test(String(e.message)) ? 'bad_request' : null
      setDownloadError(code === 'bad_request' ? 'The server rejected the filters. Check the dates and statuses.' : `Download failed: ${e.message ?? e}`)
    } finally {
      setDownloading(false)
    }
  }, [paramsKey, limit]) // eslint-disable-line react-hooks/exhaustive-deps

  const total = counts?.total ?? 0
  const willDownload = Math.min(total, limit)
  const noStatuses = statuses.size === 0
  const scopeLabel = account
    ? accountLabel(accounts.find((a) => a.company === account) ?? { company: account })
    : channel ? `all ${CHANNEL_LABEL[channel]} accounts` : 'all channels and accounts'

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h6" gutterBottom>Prompt Improvement</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Export decided AI suggestions as one JSON line each: the inbound message, the draft, the grader&apos;s
        scores and notes, and the text the reviewer actually sent, with an edit ratio between draft and final.
        Every text field is PII-redacted with the same two-layer pass the admin assistant uses. If the
        name/address pass is unavailable, the field is withheld and listed in that row&apos;s <code>withheld</code> array,
        never sent regex-only.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <ToggleButtonGroup size="small" exclusive value={channel} onChange={onChannel} aria-label="Channel">
              <ToggleButton value="">All channels</ToggleButton>
              {CHANNELS.map((c) => <ToggleButton key={c.value} value={c.value}>{c.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <FormControl size="small" fullWidth>
              <InputLabel id="prompt-review-account">Account</InputLabel>
              <Select labelId="prompt-review-account" label="Account" value={account} onChange={(e) => setAccount(e.target.value)}>
                <MenuItem value="">{channel ? `All ${CHANNEL_LABEL[channel]} accounts` : 'All accounts'}</MenuItem>
                {groupedAccounts.flatMap(([ch, list]) => [
                  !channel && <ListSubheader key={`h-${ch}`}>{CHANNEL_LABEL[ch] ?? ch}</ListSubheader>,
                  ...list.map((a) => (
                    <MenuItem key={a.company} value={a.company}>
                      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ width: '100%' }}>
                        <span>{accountLabel(a)}</span>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{a.company}</Typography>
                      </Stack>
                    </MenuItem>
                  )),
                ].filter(Boolean))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small" fullWidth type="datetime-local" label="Decided from" InputLabelProps={{ shrink: true }}
              value={from} onChange={(e) => setFrom(e.target.value)}
            />
            <TextField
              size="small" fullWidth type="datetime-local" label="Decided to" InputLabelProps={{ shrink: true }}
              value={to} onChange={(e) => setTo(e.target.value)}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="prompt-review-limit">Max rows</InputLabel>
              <Select labelId="prompt-review-limit" label="Max rows" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                {LIMITS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          <FormGroup row>
            {STATUSES.map((s) => (
              <FormControlLabel
                key={s.value}
                control={<Checkbox size="small" checked={statuses.has(s.value)} onChange={() => toggleStatus(s.value)} />}
                label={<span title={s.hint}>{s.label}{counts ? ` (${counts[s.value] ?? 0})` : ''}</span>}
              />
            ))}
          </FormGroup>
        </Stack>
      </Paper>

      {countsError && <Alert severity="warning" sx={{ mb: 2 }}>Could not count matching rows: {countsError}</Alert>}
      {noStatuses && <Alert severity="info" sx={{ mb: 2 }}>{ERR_TEXT.invalid_status}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          onClick={onDownload}
          disabled={downloading || noStatuses || total === 0}
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {downloading ? 'Redacting and downloading…' : `Download ${willDownload} row${willDownload === 1 ? '' : 's'} (.jsonl)`}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {counts ? `${total} match for ${scopeLabel}` : `Counting for ${scopeLabel}…`}
          {counts && total > limit ? `; the newest ${limit} are exported. Narrow the dates or raise Max rows.` : ''}
        </Typography>
      </Stack>

      {downloading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Each text field is sent to the PII pass one row at a time, so a large export takes a minute or two.
        </Typography>
      )}
      {downloaded && <Alert severity="success" sx={{ mt: 2 }}>Saved {downloaded}. The last line is a <code>summary</code> row with counts.</Alert>}
      {downloadError && <Alert severity="error" sx={{ mt: 2 }}>{downloadError}</Alert>}

      <Typography variant="subtitle2" sx={{ mt: 4, mb: 1 }}>What a row holds</Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        <ul style={{ marginTop: 0 }}>
          <li><code>account</code>, <code>channel</code>, <code>status</code>, <code>intent</code>, <code>intentConfidence</code>, <code>flowKey</code>, <code>disposition</code> vs <code>aiDisposition</code></li>
          <li><code>inboundMessage</code>, <code>draftReply</code>, <code>humanReply</code> (redacted), <code>replyChanged</code>, <code>editRatio</code> (0 untouched, 1 rewritten; the signature swap does not count; null when the flow produced no draft, see <code>draftEmpty</code>)</li>
          <li><code>placeholdersLeft</code>: template tokens such as <code>INSERTEDD</code> the drafter shipped unfilled</li>
          <li><code>grade</code>: recommendation, overall score, the four dimension scores, and the grader&apos;s redacted notes</li>
          <li><code>models</code>: which deployments drafted and graded, whether TRQ matched an order</li>
          <li><code>decidedBy</code> is <code>human</code> or the automation stamp, never a name; there are no per-agent fields</li>
        </ul>
      </Typography>
    </Box>
  )
}
